<script>
import api from '@/api'
import { alertSuccess, alertError } from '@/helpers/swal'

export default {
  data() {
    return {
      pagetitle: 'Roles',
      authorities: [],
      selectAll: false,
      readOnly: false,
      role: {
        id: null,
        roleName: '',
        strAuthorities: [],
      },
      errors: {
        roleName: '',
      },
      saveBtnDisabled: false,
    }
  },
  async mounted() {
    await this.getAuthorities()

    // In case of edit role
    const roleId = this.$route.params.id
    if (roleId) {
      await this.getRoleData(roleId)
    }
  },
  methods: {
    async getAuthorities() {
      await api.get(`/admin/roles/type/${this.authUser.gid}/authorities.json`).then((response) => {
        this.authorities = response.data
      })
    },

    async getRoleData(roleId) {
      await api.get(`/admin/roles/get/${roleId}.json`).then((response) => {
        this.role = response.data
        this.setPermissions(this.role.strAuthorities)
      })
    },

    saveRole() {
      if (!this.validateRequest()) {
        alertError('Please fill in all the required fields')
        return
      }

      this.saveBtnDisabled = true
      let permissions = []

      this.authorities.forEach((module) => {
        if (module.checked) permissions.push(module.slug)
        if (module.Authority) {
          module.Authority.forEach((category) => {
            if (category.checked) permissions.push(category.slug)
            if (category.Authority) {
              category.Authority.forEach((permission) => {
                if (permission.checked) permissions.push(permission.slug)
              })
            }
          })
        }
      })

      this.role.id ? this.updateRole(permissions) : this.addRole(permissions)
    },

    addRole(permissions) {
      let data = {
        roleName: this.role.roleName,
        managedRole: false,
        managedTypeId: null,
        adminGroup: {
          id: null,
        },
        strAuthorities: permissions,
      }

      api
        .post('/admin/roles/add.json', data)
        .then(() => {
          alertSuccess('Role added successfully')
          this.$router.push({ name: 'roles' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    updateRole(permissions) {
      let data = this.role
      data.strAuthorities = permissions

      api
        .post('/admin/roles/update.json', data)
        .then(() => {
          alertSuccess('Role updated successfully')
          this.$router.push({ name: 'roles' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    validateRequest() {
      let isValid = true
      this.errors = {}
      if (!this.role.roleName) {
        this.errors.roleName = 'Name field is required'
        isValid = false
      }
      return isValid
    },

    setPermissions(permissions) {
      this.authorities.forEach((module) => {
        module.checked = permissions.includes(module.slug)
        if (module.Authority) {
          module.Authority.forEach((category) => {
            category.checked = permissions.includes(category.slug)
            if (category.Authority) {
              category.Authority.forEach((permission) => {
                permission.checked = permissions.includes(permission.slug)
              })
            }
          })
        }
      })

      this.checkAllSelected()
      this.checkAllReadOnly()
    },

    checkAllSelected() {
      this.selectAll = this.authorities.every(
        (module) => module.checked && (module.Authority?.every((category) => category.checked && (category.Authority?.every((permission) => permission.checked) ?? true)) ?? true)
      )
    },

    checkAllReadOnly() {
      this.readOnly = (function isReadOnly(item) {
        return (item.readOnly ? item.checked : !item.checked) && (!item.Authority || item.Authority.every(isReadOnly))
      })({ Authority: this.authorities })
    },

    toggleAll() {
      if (this.readOnly) this.readOnly = false
      this.authorities.forEach((module) => {
        module.checked = this.selectAll
        if (module.Authority) {
          module.Authority.forEach((category) => {
            category.checked = this.selectAll
            if (category.Authority) {
              category.Authority.forEach((permission) => {
                permission.checked = this.selectAll
              })
            }
          })
        }
      })
    },

    toggleReadOnly() {
      if (this.selectAll) this.selectAll = false
      this.authorities.forEach((module) => {
        module.checked = module.readOnly ? this.readOnly : false
        if (module.Authority) {
          module.Authority.forEach((category) => {
            category.checked = category.readOnly ? this.readOnly : false
            if (category.Authority) {
              category.Authority.forEach((permission) => {
                permission.checked = permission.readOnly ? this.readOnly : false
              })
            }
          })
        }
      })
    },

    // Toggle all permissions in a module
    toggleModule(module) {
      if (module.Authority) {
        module.Authority.forEach((category) => {
          category.checked = module.checked
          if (category.Authority) {
            category.Authority.forEach((permission) => {
              permission.checked = module.checked
            })
          }
        })
      }

      this.checkAllSelected()
      this.checkAllReadOnly()
    },

    // Toggle all permissions in a category
    toggleCategory(module, category) {
      if (category.Authority) {
        category.Authority.forEach((permission) => {
          permission.checked = category.checked
        })
      }

      // If some categories are checked, check the module
      module.checked = module.Authority.some((cat) => cat.checked)

      this.checkAllSelected()
      this.checkAllReadOnly()
    },

    // Update category and module based on permission changes
    togglePermission(module, category, permission) {
      if (permission.checked) {
        category.checked = true
        module.checked = true
      }

      this.checkAllSelected()
      this.checkAllReadOnly()
    },

    // Toggle collapse/expand
    toggleCollapse(item) {
      item.expanded = !item.expanded
    },
  },
}
</script>

<template>
  <Teleport to="#elePageTitle">
    {{ pagetitle }}
  </Teleport>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header d-flex align-items-center">
          <h4 class="card-title mb-0">{{ $route.params.id ? 'Edit Role' : 'Add New Role' }}</h4>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveRole()">
            <div class="mb-3">
              <label for="name-input" class="form-label">Name</label>
              <input type="text" class="form-control" placeholder="Enter name" id="name-input" v-model="role.roleName" />
              <div v-if="errors.roleName" class="invalid-feedback">{{ errors.roleName }}</div>
            </div>
            <label>Permissions</label>
            <div class="row row-cols-md-auto gx-4 gy-2 align-items-center ms-auto mb-2">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="select-all" v-model="selectAll" @change="toggleAll()" />
                <label class="form-check-label" for="select-all">All</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="read-only" v-model="readOnly" @change="toggleReadOnly()" />
                <label class="form-check-label" for="read-only">Read only</label>
              </div>
            </div>
            <ul class="list-group">
              <li v-for="(module, moduleIndex) in authorities" :key="moduleIndex" class="list-group-item">
                <div class="d-flex align-items-center">
                  <input type="checkbox" :id="'module-' + moduleIndex" v-model="module.checked" @change="toggleModule(module)" class="me-2 form-check-input" />
                  <label :for="'module-' + moduleIndex" class="mb-0 form-check-label">
                    <strong>{{ module.title }}</strong>
                  </label>
                  <!-- <button class="btn btn-sm btn-outline-primary ms-auto" @click.prevent="toggleCollapse(module)">
                      {{ module.expanded ? "−" : "+" }}
                    </button> -->
                </div>
                <small class="text-muted ms-4">{{ module.description }}</small>
                <ul v-show="module.Authority" class="list-group mt-2 ms-2 ms-md-4">
                  <li v-for="(category, categoryIndex) in module.Authority" :key="categoryIndex" class="list-group-item">
                    <div class="d-flex align-items-center">
                      <input
                        type="checkbox"
                        :id="'category-' + moduleIndex + '-' + categoryIndex"
                        v-model="category.checked"
                        @change="toggleCategory(module, category)"
                        class="me-2 form-check-input"
                      />
                      <label :for="'category-' + moduleIndex + '-' + categoryIndex" class="mb-0 form-check-label">{{ category.title }}</label>
                      <!-- <button class="btn btn-sm btn-outline-secondary ms-auto" @click.prevent="toggleCollapse(category)">
                          {{ category.expanded ? "−" : "+" }}
                        </button> -->
                    </div>
                    <small class="text-muted ms-4">{{ category.description }}</small>
                    <ul v-show="category.Authority" class="list-group mt-2 ms-2 ms-md-4">
                      <li v-for="(permission, permIndex) in category.Authority" :key="permIndex" class="list-group-item">
                        <div class="d-flex align-items-center">
                          <input
                            type="checkbox"
                            :id="'permission-' + moduleIndex + '-' + categoryIndex + '-' + permIndex"
                            v-model="permission.checked"
                            @change="togglePermission(module, category, permission)"
                            class="me-2 form-check-input"
                          />
                          <label :for="'permission-' + moduleIndex + '-' + categoryIndex + '-' + permIndex" class="mb-0 form-check-label">{{ permission.title }}</label>
                        </div>
                        <small class="text-muted ms-4">{{ permission.description }}</small>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </form>
        </div>
        <div class="card-footer">
          <div class="row">
            <div class="col-7">
              <router-link to="/roles" class="btn btn-light d-inline-flex align-items-center">
                <i class="bx bx-arrow-back font-size-16 me-1"></i>
                Back to List
              </router-link>
            </div>
            <div class="col-5 d-flex justify-content-end">
              <button class="btn btn-success d-flex align-items-center" @click.prevent="saveRole()" :disabled="saveBtnDisabled">
                <i v-if="!saveBtnDisabled" class="bx bx-check-double font-size-16"></i>
                <i v-else class="bx bx-loader bx-spin font-size-16"></i>
                <span class="ms-1">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
